import { FirmDailyNavConfigUpdate } from 'api';

// The API stores thresholds as a fraction of 1 (0.10 = 10%, bounded to
// [0, 1]) with up to 4 decimal places. The UI shows/accepts a percentage
// with up to 2 decimal places so the round trip through /100 and *100 never
// loses precision or exceeds the API's 4 decimal places.
export const THRESHOLD_PATTERN = /^\d{1,3}(\.\d{1,2})?$/;
export const THRESHOLD_MAX_PERCENT = 100;

export const isValidThresholdPercent = (value: string): boolean => {
  const trimmed = value.trim();
  return THRESHOLD_PATTERN.test(trimmed) && Number(trimmed) <= THRESHOLD_MAX_PERCENT;
};

export const percentToFraction = (percent: string): string => (Number(percent.trim()) / 100).toFixed(4);

export const fractionToPercent = (fraction?: string | null): string =>
  fraction === null || fraction === undefined || fraction === ''
    ? ''
    : Number((Number(fraction) * 100).toFixed(4)).toString();

export type DailyNavThresholdKey =
  | 'cap_iq_short_window_delta'
  | 'cap_iq_long_window_delta'
  | 'cap_iq_since_last_valuation_delta'
  | 'secondary_short_window_delta'
  | 'secondary_long_window_delta'
  | 'secondary_since_last_valuation_delta';

export type DailyNavThresholdField = {
  key: DailyNavThresholdKey;
  label: string;
};

export type DailyNavThresholdGroup = {
  title: string;
  fields: DailyNavThresholdField[];
};

// Labels name the window a threshold is judged over ("2 Trading Sessions Δ").
// Callers pass the counts that apply to what they render — typed values in the
// live form, the saved counts in history — or nothing for the abstract wording.
const windowThresholdLabel = (
  sessions: number | string | null | undefined,
  fallback: string
): string => {
  const count = Number(sessions);
  if (!Number.isInteger(count) || count < 1) {
    return fallback;
  }
  return `${count} Trading Session${count === 1 ? '' : 's'} Δ`;
};

export const getDailyNavThresholdGroups = (
  shortWindowSessions?: number | string | null,
  longWindowSessions?: number | string | null
): DailyNavThresholdGroup[] => {
  const shortLabel = windowThresholdLabel(shortWindowSessions, 'Short Window Δ');
  const longLabel = windowThresholdLabel(longWindowSessions, 'Long Window Δ');
  return [
    {
      title: 'Capital IQ',
      fields: [
        { key: 'cap_iq_short_window_delta', label: shortLabel },
        { key: 'cap_iq_long_window_delta', label: longLabel },
        { key: 'cap_iq_since_last_valuation_delta', label: 'Since Last Valuation Δ' },
      ],
    },
    {
      title: 'Secondary Transaction',
      fields: [
        { key: 'secondary_short_window_delta', label: shortLabel },
        { key: 'secondary_long_window_delta', label: longLabel },
        { key: 'secondary_since_last_valuation_delta', label: 'Since Last Valuation Δ' },
      ],
    },
  ];
};

// Abstract-label form: supplies the keys/ordering.
export const DAILY_NAV_THRESHOLD_GROUPS: DailyNavThresholdGroup[] = getDailyNavThresholdGroups();

export const DAILY_NAV_THRESHOLD_KEYS: DailyNavThresholdKey[] = DAILY_NAV_THRESHOLD_GROUPS.flatMap(group =>
  group.fields.map(field => field.key)
);

export const DAILY_NAV_FIRM_DEFAULTS_COPY =
  'The defaults can be superseded at a company level. If no threshold is set at the company level, these defaults will be used.';

// Firm-only. Kept out of DAILY_NAV_THRESHOLD_GROUPS/_KEYS because those are
// shared with the company override form, which must never render these.
export type DailyNavWindowSessionsKey = 'short_window_sessions' | 'long_window_sessions';

export type DailyNavWindowSessionsField = {
  key: DailyNavWindowSessionsKey;
  label: string;
};

export const DAILY_NAV_WINDOW_SESSIONS_FIELDS: DailyNavWindowSessionsField[] = [
  { key: 'short_window_sessions', label: 'Short Window (trading sessions)' },
  { key: 'long_window_sessions', label: 'Long Window (trading sessions)' },
];

export const DAILY_NAV_WINDOW_SESSIONS_KEYS: DailyNavWindowSessionsKey[] = DAILY_NAV_WINDOW_SESSIONS_FIELDS.map(
  field => field.key
);

export const DAILY_NAV_DEFAULT_SHORT_WINDOW_SESSIONS = 1;
export const DAILY_NAV_DEFAULT_LONG_WINDOW_SESSIONS = 5;

export const WINDOW_SESSIONS_MIN = 1;
export const WINDOW_SESSIONS_MAX = 31;
// Whole trading sessions only; up to 2 digits covers the [1, 31] range.
export const WINDOW_SESSIONS_PATTERN = /^\d{1,2}$/;

export const isValidWindowSessions = (value: string): boolean => {
  const trimmed = value.trim();
  if (!WINDOW_SESSIONS_PATTERN.test(trimmed)) {
    return false;
  }
  const numeric = Number(trimmed);
  return numeric >= WINDOW_SESSIONS_MIN && numeric <= WINDOW_SESSIONS_MAX;
};

// Ordering only; assumes both already passed isValidWindowSessions.
export const isShortWindowBeforeLongWindow = (shortValue: string, longValue: string): boolean =>
  Number(shortValue.trim()) < Number(longValue.trim());

// The NAV day comes from a trading market, not a stored closing time — its
// calendar supplies the session close. Supported markets are NOT listed here;
// they come from the generated enum, so the picker cannot offer one the API
// would reject. Only the labels below are a frontend concern.
export type DailyNavTradingMarket = FirmDailyNavConfigUpdate.trading_calendar;
// Local alias for member access; re-exporting a const of the same name trips
// @typescript-eslint/no-redeclare and nothing else needs the members.
const TradingMarket = FirmDailyNavConfigUpdate.trading_calendar;

// A Record, not a list, so adding or removing a market backend-side is a
// compile error rather than a blank dropdown row.
const TRADING_MARKET_LABELS: Record<DailyNavTradingMarket, string> = {
  [TradingMarket.XNYS]: 'New York Stock Exchange (XNYS)',
  [TradingMarket.XNAS]: 'Nasdaq (XNAS)',
  [TradingMarket.XTSE]: 'Toronto Stock Exchange (XTSE)',
  [TradingMarket.XLON]: 'London Stock Exchange (XLON)',
  [TradingMarket.XETR]: 'Deutsche Börse Xetra (XETR)',
  [TradingMarket.XPAR]: 'Euronext Paris (XPAR)',
  [TradingMarket.XTKS]: 'Tokyo Stock Exchange (XTKS)',
  [TradingMarket.XHKG]: 'Hong Kong Stock Exchange (XHKG)',
  [TradingMarket.XASX]: 'Australian Securities Exchange (XASX)',
};

export const DAILY_NAV_TRADING_MARKET_OPTIONS: { value: DailyNavTradingMarket; label: string }[] =
  Object.values(TradingMarket).map(value => ({
    value,
    label: TRADING_MARKET_LABELS[value],
  }));

// Falls back to the raw code so an unsupported value (only possible from data
// predating the restriction) renders as itself rather than blank.
export const tradingMarketLabel = (value?: string | null): string =>
  (value && TRADING_MARKET_LABELS[value as DailyNavTradingMarket]) || value || '—';

// Seeds the form for a firm with no saved template; also what existing config
// versions were backfilled to.
export const DAILY_NAV_DEFAULT_TRADING_MARKET: DailyNavTradingMarket =
  TradingMarket.XNYS;
